import React from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../../context/FeedbackContext';
import {
  buildCustomShapesSavePayload,
  createEmptyCustomShape,
  normalizeShapeName,
  validateCustomShapesForm,
} from '../../utils/customShapes';

export default function ShapesSettingsPanel({
  form,
  setField,
  onSave,
  onReset,
  saving,
}) {
  const { t } = useTranslation();
  const { confirm, showError } = useFeedback();
  const shapes = form.customShapes || [];

  const updateShapes = (next) => setField('customShapes', next);

  const handleAdd = () => {
    updateShapes([...shapes, createEmptyCustomShape()]);
  };

  const handleChange = (index, value) => {
    const next = shapes.map((shape, i) => (
      i === index ? { ...shape, name: value } : shape
    ));
    updateShapes(next);
  };

  const handleDelete = async (index) => {
    const shape = shapes[index];
    const ok = await confirm({
      title: t('settings.shapes.deleteTitle'),
      message: t('settings.shapes.deleteConfirm', { name: shape.name || t('common.unnamed') }),
      confirmLabel: t('common.delete'),
      confirmColor: 'error',
    });
    if (!ok) return;
    updateShapes(shapes.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const err = validateCustomShapesForm(shapes, t);
    if (err) {
      showError(err);
      return;
    }
    onSave(buildCustomShapesSavePayload(shapes));
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        {t('settings.shapes.description')}
      </Typography>

      {shapes.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          {t('settings.shapes.empty')}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {shapes.map((shape, index) => (
            <Stack
              key={shape._id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <TextField
                label={t('settings.shapes.nameLabel')}
                value={shape.name}
                onChange={(e) => handleChange(index, e.target.value)}
                onBlur={(e) => handleChange(index, normalizeShapeName(e.target.value))}
                size="small"
                sx={{ flex: 1, minWidth: 160 }}
                inputProps={{ maxLength: 20 }}
              />
              <IconButton
                color="error"
                onClick={() => handleDelete(index)}
                aria-label={t('common.delete')}
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      <Box>
        <Button startIcon={<AddIcon />} onClick={handleAdd} size="small">
          {t('settings.shapes.add')}
        </Button>
      </Box>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
        <Button onClick={onReset} disabled={saving}>
          {t('common.reset')}
        </Button>
      </Stack>
    </Stack>
  );
}
